// Pestaña «Historial crediticio» de la ficha 360: el resumen de la cartera del cliente y la lista
// de sus créditos. «Ver detalle» abre el expediente de UNO en un modal, sin salir del cliente.
import { useState } from 'react';
import { ErrorState, Modal, Skeleton } from '@/kit';
import { CreditFile } from '@/domains/credit/creditFile';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import type { LoanBorrowerCreditItem } from '@/api/services/loan-credit';
import type { CustomerCreditDetail } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import { isGap } from '../lib/customer360Gaps';
import { EMPTY_CELL, formatMoneyShortCents } from '../lib/creditCustomersFormat';
import {
    creditArrearsOf,
    creditBalanceOf,
    creditDebtOf,
    creditHistorySummaryOf,
    creditProductOf,
    creditProgressOf,
    hasTotalDebt,
    HISTORY_STATUS_CHIP_CLASS,
    HISTORY_STATUS_LABEL,
    type CreditHistoryGap,
} from '../lib/customerCreditHistory';
import {
    CARD,
    CARD_HEAD,
    CHIP,
    Field,
    FILE_DETAIL_TRIGGER,
    FILE_MODAL_CLASS,
    FileCard,
    FileModalFrame,
    ReadFactBlock,
    ReadGap,
    UnreadValue,
} from './CustomerFileUi';

/** El vacío del mock: una sola frase, sin tarjeta y sin párrafo que la explique. */
const NO_CREDITS_MESSAGE = 'Este cliente no tiene créditos registrados.';

const TH =
    'bg-muted/40 px-2.5 py-1.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

const TD = 'whitespace-nowrap px-2.5 py-1.5 align-middle';

const TD_NUM = 'text-right tabular-nums font-semibold';

export function CustomerCreditHistoryTab({
    credit,
    onRetry,
}: {
    credit: CustomerCreditDetail;
    onRetry: () => void;
}) {
    if (credit.state === 'loading') return <Skeleton className="h-32" />;
    if (credit.state === 'forbidden') {
        return (
            <ErrorState
                title="No tienes permiso para ver el crédito de este cliente"
                description="Tu rol no incluye la lectura de créditos. Reintentar no lo cambia; contacta a quien administra los roles."
            />
        );
    }
    if (credit.state === 'no-source') {
        return (
            <ReadFactBlock
                title="Este cliente no tiene acreditado en el silo de crédito"
                description="No hay historial que leer hasta que origine su primer crédito. La lectura salió bien: esto es lo que dice."
            />
        );
    }
    if (credit.state === 'error') {
        return (
            <ErrorState
                title="No se pudo leer el crédito de este cliente"
                description="La lectura del acreditado falló. Vuelve a intentar."
                onRetry={onRetry}
            />
        );
    }

    const { credits } = credit.data;
    if (credits.length === 0) {
        return (
            <p className="px-5 py-8 text-center text-[12px] italic text-muted-foreground">
                {NO_CREDITS_MESSAGE}
            </p>
        );
    }

    return (
        <>
            <SummaryCard credits={credits} />
            <CreditsCard credits={credits} />
        </>
    );
}

function SummaryCard({ credits }: { credits: LoanBorrowerCreditItem[] }) {
    const summary = creditHistorySummaryOf(credits);

    return (
        <FileCard title="Resumen">
            <Field label="Créditos totales">{summary.total}</Field>
            <Field label="Vigentes">
                {summary.currentDebtCents === null ? (
                    <>
                        {summary.currentCount} · saldo{' '}
                        {isGap(summary.currentBalanceCents) ? (
                            <GapValue gap={summary.currentBalanceCents} />
                        ) : (
                            formatMoneyShortCents(summary.currentBalanceCents)
                        )}
                    </>
                ) : (
                    <>
                        {summary.currentCount} · deuda total{' '}
                        {formatMoneyShortCents(summary.currentDebtCents)}
                        {isGap(summary.currentBalanceCents) ? null : (
                            <span className="text-muted-foreground">
                                {' '}
                                · capital {formatMoneyShortCents(summary.currentBalanceCents)}
                            </span>
                        )}
                    </>
                )}
            </Field>
            <Field label="Liquidados">{summary.settledCount}</Field>
        </FileCard>
    );
}

function CreditsCard({ credits }: { credits: LoanBorrowerCreditItem[] }) {
    /** El crédito abierto en el modal; `null` es el modal cerrado. */
    const [openCreditId, setOpenCreditId] = useState<string | null>(null);
    const withTotalDebt = hasTotalDebt(credits);

    return (
        <section className={CARD}>
            <h3 className={cn(CARD_HEAD, 'flex items-baseline justify-between gap-2')}>
                <span>Créditos del cliente</span>
                <span className="font-normal normal-case tabular-nums">
                    {credits.length} totales
                </span>
            </h3>
            {/* Nueve columnas no entran en el panel derecho: la tabla scrollea DENTRO de la
                tarjeta en vez de recortar columnas o mandar la página al scroll horizontal. */}
            <div className="overflow-x-auto">
                <table className="w-full min-w-[840px] text-[11px]">
                    <thead>
                        <tr className="border-b border-border">
                            <th scope="col" className={cn(TH, 'text-left')}>
                                Crédito
                            </th>
                            <th scope="col" className={cn(TH, 'text-left')}>
                                Producto
                            </th>
                            <th scope="col" className={cn(TH, 'text-left')}>
                                Alta
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                Principal
                            </th>
                            <th
                                scope="col"
                                className={cn(TH, 'text-right')}
                                title={
                                    withTotalDebt
                                        ? 'Capital, interés e IVA que faltan. Abajo, el saldo insoluto (solo capital).'
                                        : undefined
                                }
                            >
                                {withTotalDebt ? 'Deuda total' : 'Saldo'}
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                Avance
                            </th>
                            <th scope="col" className={cn(TH, 'text-left')}>
                                Mora
                            </th>
                            <th scope="col" className={cn(TH, 'text-left')}>
                                Estado
                            </th>
                            <th scope="col" className={TH} />
                        </tr>
                    </thead>
                    <tbody>
                        {credits.map((credit) => (
                            <CreditRow
                                key={credit.creditId}
                                credit={credit}
                                withTotalDebt={withTotalDebt}
                                onOpen={setOpenCreditId}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={openCreditId !== null}
                onClose={() => {
                    setOpenCreditId(null);
                }}
                title="Expediente del crédito"
                size="xl"
                className={FILE_MODAL_CLASS}
            >
                <FileModalFrame>
                    <CreditFile creditId={openCreditId} />
                </FileModalFrame>
            </Modal>
        </section>
    );
}

function CreditRow({
    credit,
    withTotalDebt,
    onOpen,
}: {
    credit: LoanBorrowerCreditItem;
    withTotalDebt: boolean;
    onOpen: (creditId: string) => void;
}) {
    const product = creditProductOf(credit);
    const balance = creditBalanceOf(credit);
    const progress = creditProgressOf(credit);

    return (
        <tr className="border-b border-border/50 last:border-0">
            {/* eslint-disable-next-line no-restricted-syntax -- el folio es con lo que el analista cruza el crédito contra Cartera, y el diseño lo pinta entero */}
            <td className={cn(TD, 'font-mono text-[10.5px]')}>{credit.creditId}</td>
            <td className={TD}>{isGap(product) ? <GapValue gap={product} /> : product}</td>
            <td className={cn(TD, 'text-muted-foreground')}>{formatDateMx(credit.createdAt)}</td>
            <td className={cn(TD, TD_NUM)}>{formatMoneyShortCents(credit.financedCapitalCents)}</td>
            <td className={cn(TD, TD_NUM)}>
                {withTotalDebt ? (
                    <DebtValue debt={creditDebtOf(credit)} balance={balance} />
                ) : isGap(balance) ? (
                    <GapValue gap={balance} />
                ) : (
                    formatMoneyShortCents(balance)
                )}
            </td>
            <td className={cn(TD, TD_NUM)}>
                {isGap(progress) ? <GapValue gap={progress} /> : progress}
            </td>
            <td className={TD}>
                <ArrearsCell credit={credit} />
            </td>
            <td className={TD}>
                <span className={cn(CHIP, HISTORY_STATUS_CHIP_CLASS[credit.status])}>
                    {HISTORY_STATUS_LABEL[credit.status]}
                </span>
            </td>
            <td className={TD}>
                {/* Una tabla llena de «Ver detalle» necesita que el nombre accesible diga cuál. */}
                <button
                    type="button"
                    aria-label={`Ver detalle del crédito ${credit.creditId}`}
                    onClick={() => {
                        onOpen(credit.creditId);
                    }}
                    className={FILE_DETAIL_TRIGGER}
                >
                    Ver detalle <span aria-hidden>→</span>
                </button>
            </td>
        </tr>
    );
}

/** La deuda total y, debajo y en chico, el capital insoluto cuando lo hay. */
function DebtValue({
    debt,
    balance,
}: {
    debt: number | CreditHistoryGap;
    balance: number | CreditHistoryGap;
}) {
    return (
        <>
            <div>{isGap(debt) ? <GapValue gap={debt} /> : formatMoneyShortCents(debt)}</div>
            {isGap(balance) ? null : (
                <div className="text-[9.5px] font-normal text-muted-foreground">
                    cap. {formatMoneyShortCents(balance)}
                </div>
            )}
        </>
    );
}

function ArrearsCell({ credit }: { credit: LoanBorrowerCreditItem }) {
    const arrears = creditArrearsOf(credit);
    if (arrears.kind === 'unpublished') return <GapValue gap={arrears.gap} />;
    if (arrears.kind === 'none') {
        return (
            <span title={arrears.note} className="cursor-help text-muted-foreground">
                {EMPTY_CELL}
            </span>
        );
    }

    return (
        <span className="font-bold text-danger">
            {arrears.days}d ·{' '}
            {isGap(arrears.amount) ? (
                <GapValue gap={arrears.amount} />
            ) : (
                formatMoneyShortCents(arrears.amount)
            )}
        </span>
    );
}

/** El hueco con SU motivo en el `title`, y distinguiendo el que se leyó vacío del que no se leyó. */
function GapValue({ gap }: { gap: CreditHistoryGap }) {
    return gap.read ? <ReadGap gap={gap} /> : <UnreadValue gap={gap} />;
}
